import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { InstallPWA } from "@/components/InstallPWA";
import { WhatsAppFloatingButton } from "@/components/WhatsAppFloatingButton";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { store, persistor } from "@/store";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Profile from "./pages/Profile";
import MeetingSimulator from "./pages/MeetingSimulator";
import MeetingCover from "./pages/MeetingCover";
import MeetingPreparation from "./pages/MeetingPreparation";
import MeetingPreparationSummary from "./pages/MeetingPreparationSummary";
import PracticeExpressions from "./pages/PracticeExpressions";
import MeetingSimulation from "./pages/MeetingSimulation";
import InterviewCompleted from "./pages/InterviewCompleted";
import InterviewFeedback from "./pages/InterviewFeedback";
import QuizPage from "./pages/QuizPage";
import QuizResults from "./pages/QuizResults";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SubscriptionBillingTerms from "./pages/SubscriptionBillingTerms";
import FAQ from "./pages/FAQ";
import ForBusinesses from "./pages/ForBusinesses";
import NotFound from "./pages/NotFound";
import Pricing from "./pages/Pricing";
import { Login } from "./pages/Login";
import { ProtectedRoute } from "./pages/ProtectedRoute";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancelled from "./pages/CheckoutCancelled";
import VerifyMagicLink from "./pages/VerifyMagicLink";
import UnsubscribeSuccess from "./pages/UnsubscribeSuccess";
import UnsubscribeError from "./pages/UnsubscribeError";
import { useGoogleClientId } from "./hooks/useGoogleClientId";
import { useAzureConfig } from "./hooks/useAzureConfig";
import { useSentryInit } from "./hooks/useSentryInit";
// import { useThemeColors } from "./hooks/useThemeColors"; // DISABLED: Causing color issues
import { HeroLoading } from "./components/loading/HeroLoading";
import { logger } from "@/utils/logger";
import { CookieConsent } from "@/components/CookieConsent";

const queryClient = new QueryClient();

const App = () => {
  // Inicialização do Sentry via hook (busca assíncrona da DSN via Azure Key Vault)
  useSentryInit();

  // Apply theme colors from product config (must be called early)
  // useThemeColors(); // DISABLED: Causing color issues - using static colors from index.css

  // Carregar Azure config do Lovable Cloud
  const {
    config: azureConfig,
    loading: configLoading,
    error: configError,
  } = useAzureConfig();

  const {
    clientId: googleClientId,
    loading: googleLoading,
    error: googleError,
  } = useGoogleClientId();

  // Mostrar erro se falhar ao carregar Azure config
  // if (configError) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center">
  //       <div className="text-center">
  //         <p className="text-destructive mb-2">
  //           Failed to load Azure configuration
  //         </p>
  //         <p className="text-sm text-muted-foreground">{configError}</p>
  //         <p className="text-xs text-muted-foreground mt-4">
  //           Make sure VITE_AZURE_FUNCTIONS_URL and VITE_AZURE_FUNCTIONS_KEY
  //           secrets are configured in Lovable Cloud.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  if (googleError) {
    logger.error(
      "Failed to load Google Client ID from Azure Key Vault:",
      googleError
    );
  }

  // Show loading state while loading config or Google Client ID
  if (configLoading || googleLoading) {
    return <HeroLoading />;
  }

  return (
    <HelmetProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <GoogleOAuthProvider clientId={googleClientId || ""}>
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                {/* <InstallPWA /> */}
                <BrowserRouter>
                  <CookieConsent />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/auth/passwordless/:code"
                      element={<VerifyMagicLink />}
                    />
                    <Route
                      path="/meetings"
                      element={<Navigate to="/meeting-simulator" replace />}
                    />
                    <Route
                      path="/meeting-simulator"
                      element={<MeetingSimulator />}
                    />
                    {/* Protected Routes */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route element={<ProtectedRoute requireAdmin={true} />}>
                        <Route path="/admin" element={<AdminDashboard />} />
                      </Route>
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/user" element={<Dashboard />} />
                      <Route
                        path="/meeting/:meetingId"
                        element={<MeetingCover />}
                      />
                      <Route
                        path="/meeting/:meetingId/prepare"
                        element={<MeetingPreparation />}
                      />
                      <Route
                        path="/meeting/:meetingId/summary"
                        element={<MeetingPreparationSummary />}
                      />
                      <Route
                        path="/meeting/:meetingId/practice-expressions"
                        element={<PracticeExpressions />}
                      />
                      <Route
                        path="/meeting/:meetingId/simulate"
                        element={<MeetingSimulation />}
                      />
                      <Route
                        path="/interview-completed"
                        element={<InterviewCompleted />}
                      />
                      <Route
                        path="/interview-feedback/:assessmentId"
                        element={<InterviewFeedback />}
                      />
                      <Route
                        path="/meeting/:meetingId/feedback/:assessmentId"
                        element={<InterviewFeedback />}
                      />
                      <Route
                        path="/meeting/:meetingId/feedback"
                        element={<MeetingPreparationSummary />}
                      />
                    </Route>

                    {/* Quiz Routes - Public */}
                    <Route
                      path="/meeting/:meetingId/quiz/:quizId"
                      element={<QuizPage />}
                    />
                    <Route
                      path="/meeting/:meetingId/quiz/:quizId/results"
                      element={<QuizResults />}
                    />

                    {/* Home can be public or protected - currently public */}
                    <Route path="/" element={<Index />} />

                    {/* Privacy Policy */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/terms-of-service"
                      element={<TermsOfService />}
                    />
                    <Route
                      path="/subscription-billing"
                      element={<SubscriptionBillingTerms />}
                    />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/for-businesses" element={<ForBusinesses />} />
                    <Route
                      path="/checkout/success"
                      element={<CheckoutSuccess />}
                    />
                    <Route
                      path="/checkout/cancelled"
                      element={<CheckoutCancelled />}
                    />

                    <Route
                      path="/unsubscribe-success"
                      element={<UnsubscribeSuccess />}
                    />
                    <Route
                      path="/unsubscribe-error"
                      element={<UnsubscribeError />}
                    />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>

                  {/* Global Components - Render on all pages */}
                  <WhatsAppFloatingButton />
                </BrowserRouter>
              </TooltipProvider>
            </QueryClientProvider>
          </GoogleOAuthProvider>
        </PersistGate>
      </Provider>
    </HelmetProvider>
  );
};

export default App;
